import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { Button } from './Button';
import { FormField } from './FormField';
import { useFormSubmission } from '../hooks/useFormSubmission';
import { formRecipient } from '../data/formSettings';
import type { FormConfig } from '../types/forms';
const ease = [0.23, 1, 0.32, 1] as const;
type ConfiguredFormProps = {
  config: FormConfig;
};
export function ConfiguredForm({
  config
}: ConfiguredFormProps) {
  const form = useFormSubmission(config);
  const submitting = form.status === 'submitting';
  return <div className="border border-gold/30 bg-mist p-6 sm:p-10" data-id="element-86" data-mp="1jt7ds9vox8v2">
      <AnimatePresence mode="wait" initial={false} data-id="element-87" data-mp="1ezcvnavqh1y3">
        {form.status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease
      }} role="status" aria-live="polite" className="py-6" data-id="element-88" data-mp="r9s1rwy1kka1">
            <CheckCircle2Icon size={36} strokeWidth={1.25} className="text-gold-dark" aria-hidden="true" data-id="element-89" data-mp="1sbr60haogoac" />
            <h3 className="mt-6 font-serif text-3xl font-medium text-navy" data-id="element-90" data-mp="1x4sm2smmetvi">{config.successTitle}</h3>
            <p className="mt-4 max-w-md leading-relaxed text-ink/75" data-id="element-91" data-mp="222bz5p7l10lv">{config.successBody}</p>
            <Button variant="outline" onClick={form.reset} className="mt-8" data-id="element-92" data-mp="15enuoa0g16ks">
              Submit Another
            </Button>
          </motion.div> : <motion.form key="form" noValidate onSubmit={form.handleSubmit} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease
      }} aria-label={config.formName} aria-busy={submitting} className="grid gap-6 sm:grid-cols-2" data-id="element-93" data-mp="1ed9k8p4c1lv2">
            {config.fields.map(field => <FormField key={field.name} id={`${config.id}-${field.name}`} field={field} value={form.values[field.name]} error={form.errors[field.name]} onChange={v => form.setField(field.name, v)} onBlur={() => form.blurField(field.name)} className={field.fullWidth || field.type === 'textarea' || field.type === 'checkbox' ? 'sm:col-span-2' : ''} data-id="element-94" data-mp="255q74nt2pkfw" />)}

            <div aria-hidden="true" className="hidden" data-id="element-95" data-mp="1w4a08q1gw0gf">
              <label data-id="element-96" data-mp="1rbyxlffce0k6">
                Leave this field empty
                <input type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={e => form.setHoneypot(e.target.value)} data-id="element-97" data-mp="1rae7q33z35sn" />
              </label>
            </div>

            {form.status === 'error' && <div role="alert" className="flex gap-3 border border-danger/30 bg-white p-4 text-sm sm:col-span-2" data-id="element-98" data-mp="3f1wt7bxp1sfp">
                <AlertCircleIcon size={18} strokeWidth={1.5} className="mt-0.5 shrink-0 text-danger" aria-hidden="true" data-id="element-99" data-mp="30awame38gi5u" />
                <div data-id="element-100" data-mp="3t1o4v60qmwqj">
                  <p className="font-medium text-danger" data-id="element-101" data-mp="3t2fxq92yagd4">Your message couldn’t be sent.</p>
                  <p className="mt-1 leading-relaxed text-ink/75" data-id="element-102" data-mp="1vd8voal4m62">
                    Please try again, or email us directly at{' '}
                    <a href={`mailto:${formRecipient}`} className="font-medium text-navy underline decoration-gold underline-offset-4" data-id="element-103" data-mp="3k0ggbfo0xa9n">
                      {formRecipient}
                    </a>
                    .
                  </p>
                  {form.serverMessage && <p className="mt-2 text-xs text-ink/60" data-id="element-104" data-mp="2cnxvpkurhia1">Details: {form.serverMessage}</p>}
                </div>
              </div>}

            <div className="flex flex-col gap-5 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-105" data-mp="2o32in9siorv4">
              <p className="max-w-xs text-xs leading-relaxed text-ink/60" data-id="element-106" data-mp="2j3q194kf0czx">
                Fields marked * are required. Submissions are directed to {formRecipient}.
              </p>
              <Button type="submit" disabled={submitting} className="sm:min-w-[210px]" data-id="element-107" data-mp="3btotf8twoq8t">
                {submitting ? <>
                    <Loader2Icon size={18} className="animate-spin" aria-hidden="true" data-id="element-108" data-mp="36ulpjrpnwq2f" />
                    Sending…
                  </> : config.submitLabel}
              </Button>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}